import {
  Box,
  Button,
  Group,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '../../../core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faNoteSticky, faCheck } from '@fortawesome/pro-regular-svg-icons';

export interface QuoteNoteProps {
  readonly title?: string;
  readonly body?: string;
  /** When true the note is locked into its read-only "Note added" state. */
  readonly saved?: boolean;
  /** In-flight Add Note dispatch — data-bound to the surface's per-component
   *  in-flight flag (set true on dispatch, cleared on BOTH success and failure),
   *  so the "Saving…" affordance never sticks. */
  readonly saving?: boolean;
  /** Push edits back into the surface data model (the a2ui impl wires these to
   *  `dataContext.set('/<noteId>/title|body')`), so the AddNote action's
   *  path-bound context reads the user's current text. */
  readonly onTitleChange?: (v: string) => void;
  readonly onBodyChange?: (v: string) => void;
  readonly onSave?: () => void;
}

/**
 * Quote Note — an editable note the agent attaches inside a generated
 * QuoteOption (via updateComponents) after a copy quote is generated.
 *
 * Fully controlled (mirrors EmailComposer): `title`/`body` are the data-bound
 * values and every edit is written straight back to the surface data model via
 * `onTitleChange`/`onBodyChange`. There is no local copy, so the value the user
 * sees is exactly what Add Note (the `AddNote` action, reading the same bound
 * paths) will persist — no stale-draft risk. On success the agent re-declares
 * this component with `saved` to lock it read-only.
 */
export function QuoteNote({
  title = '',
  body = '',
  saved = false,
  saving = false,
  onTitleChange,
  onBodyChange,
  onSave,
}: QuoteNoteProps) {
  const locked = saved || saving;

  return (
    <Box
      p="sm"
      style={{
        border: '1px solid var(--mantine-color-blue-2)',
        borderRadius: 8,
      }}
    >
      <Group justify="space-between" mb="xs">
        <Group gap={6}>
          <FontAwesomeIcon
            icon={faNoteSticky}
            style={{
              width: 14,
              height: 14,
              color: 'var(--mantine-color-blue-6)',
            }}
          />
          <Text fw={700} fz={12} c="blue.6" style={{ letterSpacing: 0.5 }}>
            QUOTE NOTE
          </Text>
        </Group>
        {saved && (
          <Group gap={4}>
            <FontAwesomeIcon icon={faCheck} style={{ width: 11, height: 11 }} />
            <Text fz={12} c="dimmed">
              Note added
            </Text>
          </Group>
        )}
      </Group>

      <Stack gap="xs">
        <TextInput
          value={title}
          onChange={(e) => onTitleChange?.(e.currentTarget.value)}
          readOnly={locked}
          disabled={locked}
          size="sm"
        />
        <Textarea
          value={body}
          onChange={(e) => onBodyChange?.(e.currentTarget.value)}
          readOnly={locked}
          disabled={locked}
          autosize
          minRows={3}
          size="sm"
        />
        {!saved && (
          <Button
            variant="light"
            color="blue"
            size="xs"
            onClick={() => onSave?.()}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Add Note'}
          </Button>
        )}
      </Stack>
    </Box>
  );
}
